import React, { useEffect, useMemo } from "react";
import { Avatar, Button, Menu, Select, Text } from "@mantine/core";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  IconArrowBack,
  IconChartBar,
  IconUsersGroup,
  IconChevronDown,
  IconLock,
  IconLogout,
  IconSwitchHorizontal,
  IconSettings,
} from "@tabler/icons-react";

import { assetsPrefix } from "../../utilities/utilities";
import { selectUserData } from "../../redux/selectors";
import {
  deleteCookie,
  getCookiesSession,
  setCookiesSession,
} from "../../cookiesSession";

import { changeUserRole } from "../../utilities/apis/users";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";

import actions from "../../redux/actions";
import { getEnv } from "../../utilities/env";

const getAvatarColor = (name) => {
  if (!name) return "bg-blue-500";
  const colorsList = [
    "bg-indigo-500",
    "bg-blue-500",
    "bg-teal-500",
    "bg-green-500",
    "bg-orange-500",
    "bg-pink-500",
    "bg-purple-500",
    "bg-cyan-500",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colorsList.length;
  return colorsList[index];
};

const AppHeader = ({ hide = false, hideLogo = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pathname = useLocation().pathname;
  const currentUser = useSelector(selectUserData) || {};
  const sessionData = getCookiesSession("trueCounselUserDataMaster");

  const userData = useMemo(() => {
    if (!sessionData) return null;
    try {
      return JSON.parse(sessionData)?.user || null;
    } catch {
      return null;
    }
  }, [sessionData]);

  useEffect(() => {
    if (!userData) {
      navigate("/login");
    }
  }, [navigate, userData]);

  useEffect(() => {
    if (
      userData &&
      (!currentUser?.userRole || currentUser.userRole.length === 0)
    ) {
      dispatch({
        type: actions.SET_USER_DATA,
        payload: userData,
      });
    }
  }, [userData]);

  const roles = currentUser?.userRole || [];

  const isSystemAdmin =
    currentUser?.isSystemAdmin == 1 ||
    currentUser?.userRole?.some((role) => role.name === "System-Admin");

  const logoutHandler = () => {
    deleteCookie("trueCounselUserDataMaster");
    window.location.href = getEnv("REACT_APP_LANDING_PAGE_URL");
  };

  const handleRoleChange = async (roleId) => {
    try {
      const response = await apiWrapWithErrorWithData(changeUserRole(roleId));
      const result = response?.result;

      if (response?.success && result?.token && result?.user) {
        setCookiesSession(
          "trueCounselUserDataMaster",
          JSON.stringify(result),
          2,
        );

        dispatch({
          type: actions.SET_USER_DATA,
          payload: result.user,
        });

        navigate(0);
      }
    } catch (error) {
      console.error("Role switch failed:", error);
    }
  };

  const adminButtons = [
    {
      show: isSystemAdmin && window.location.pathname !== "/app/choose_manager",
      label: "Back to Main",
      icon: <IconArrowBack size={16} />,
      path: "/app/choose_manager",
    },
    {
      show: isSystemAdmin,
      label: "Analytics",
      icon: <IconChartBar size={16} />,
      path: "/system-analytics",
    },
    {
      show: isSystemAdmin,
      label: "System Configuration",
      icon: <IconSettings size={16} />,
      path: "/system-configuration/team-manager",
    },
  ];

  const show = pathname.includes("/app/choose_manager");
  const avatarBg = getAvatarColor(userData?.name);

  const controls = (
    <div className="flex items-center gap-4">
      {!show && (
        <Select
          required
          className="w-64 disabled:opacity-100"
          placeholder="Select Role to login as"
          withinPortal
          data={roles.map((role) => ({
            label: role.name,
            value: role.id.toString(),
          }))}
          value={currentUser?.loginAs?.id?.toString()}
          onChange={handleRoleChange}
        />
      )}

      {isSystemAdmin &&
        show &&
        adminButtons.map(
          (button) =>
            button.show && (
              <Button
                key={button.label}
                onClick={() => navigate(button.path)}
                variant={
                  window.location.pathname === button.path ? "filled" : "subtle"
                }
                color="blue"
                leftIcon={button.icon}
                size="sm"
                className="rounded-lg px-4 font-medium"
              >
                {button.label}
              </Button>
            ),
        )}

      <Menu shadow="md" width={180} position="bottom-end" radius="md">
        <Menu.Target>
          <div className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-400 py-1 px-2 transition-all hover:border-gray-100 hover:bg-gray-50/80">
            {userData?.picture ? (
              <Avatar size="sm" src={userData.picture} radius="xl" />
            ) : (
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full ${avatarBg} text-xs font-semibold text-white shadow-inner`}
              >
                {userData?.name?.[0]?.toUpperCase()}
              </div>
            )}

            <div className="ml-1 flex flex-col">
              <Text size="sm">{userData?.name}</Text>
              <Text size="xs">{userData?.email}</Text>
            </div>
            <IconChevronDown size={14} className="ml-0.5 text-gray-400" />
          </div>
        </Menu.Target>

        <Menu.Dropdown className="border border-gray-100 p-1">
          <Menu.Item
            icon={<IconSwitchHorizontal size={14} className="text-gray-500" />}
            onClick={() => navigate("/app/choose_manager")}
            className="rounded text-gray-700 hover:bg-gray-50"
          >
            Choose Manager
          </Menu.Item>

          <Menu.Item
            icon={<IconLock size={14} className="text-gray-500" />}
            onClick={() => navigate("/app/updatePassword")}
            className="rounded text-gray-700 hover:bg-gray-50"
          >
            Update Password
          </Menu.Item>

          <Menu.Divider className="border-gray-100" />

          <Menu.Item
            icon={<IconLogout size={14} className="text-red-500" />}
            onClick={logoutHandler}
            className="rounded text-red-600 hover:bg-red-50/50"
          >
            Logout
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </div>
  );

  if (hideLogo) {
    return (
      <div className="flex h-full w-full items-center justify-end px-4">
        {controls}
      </div>
    );
  }

  return (
    <div className="sticky top-0 z-50 flex flex-row items-center justify-between border-b border-gray-100 bg-white/90 px-8 py-3 shadow-sm backdrop-blur-md transition-all">
      <Link to="/app/choose_manager">
        <img
          className="w-48 object-contain"
          src={`${assetsPrefix}/images/trueCounselLogo.png`}
          alt="TrueCounsel"
        />
      </Link>

      {controls}
    </div>
  );
};

export default AppHeader;
